import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { MultiValue } from 'react-select'
import {
  faBarsProgress,
  faCartShopping,
  faLaptopMobile,
  faTag,
  faTags,
  faTrademark,
} from '@fortawesome/pro-solid-svg-icons'
import type { CurrentRefinementsConnectorParamsRefinement } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { observer } from 'mobx-react'

import { FilterColumn } from 'Components/FilterColumn/FilterColumn'
import { useTaggingEnabled } from 'Components/Tagging/useTaggingEnabled'

import {
  getCaseItemStatusTranslationId,
  getCaseItemTypeTranslationId,
} from 'Utilities'

import {
  E2E_ORDER_FILTER_COPAYMENT,
  E2E_ORDER_FILTER_DATE,
  E2E_ORDER_FILTER_DATE_TOOLBAR,
  E2E_ORDER_FILTER_DEVICE_TYPE,
  E2E_ORDER_FILTER_MANUFACTURER,
  E2E_ORDER_FILTER_MODEL,
  E2E_ORDER_FILTER_STATUS,
  E2E_ORDER_FILTER_TAGS,
  E2E_ORDER_FILTER_TYPE,
} from 'Constants/e2e'

import { MultidatepickerFilter } from 'Algolia/filters/MultidatepickerFilter/MultidatepickerFilter'
import { MultiselectFilter } from 'Algolia/filters/MultiselectFilter/MultiselectFilter'
import { RangeFilter } from 'Algolia/filters/RangeFilter/RangeFilter'

import type { OrdersFilterColumnProps } from './OrdersFilterColumn.types'

const createItemsTransformer =
  (
    intl: ReturnType<typeof useIntl>,
    getTranslationId: (value: string) => string,
  ) =>
  (items: RefinementListItem[]) =>
    items.map(item => ({
      ...item,
      label: intl.formatMessage({
        id: getTranslationId(item.value.toString()),
      }),
    }))

const createValueMapper =
  (
    intl: ReturnType<typeof useIntl>,
    getTranslationId: (value: string) => string,
  ) =>
  (
    result: MultiValue<RefinementListItem>,
    item: CurrentRefinementsConnectorParamsRefinement,
  ) => {
    const transformedItem: RefinementListItem = {
      count: item.count ?? 0,
      isRefined: true,
      label: intl.formatMessage({
        id: getTranslationId(item.value.toString()),
      }),
      value: item.value.toString(),
    }

    return [...result, transformedItem]
  }

export const OrdersFilterColumn = observer((props: OrdersFilterColumnProps) => {
  const { onCloseFilterColumn, isVisible } = props
  const intl = useIntl()
  const isTaggingEnabled = useTaggingEnabled()

  const transformCaseItemTypeItems = useMemo(
    () => createItemsTransformer(intl, getCaseItemTypeTranslationId),
    [intl],
  )

  const caseItemTypeValueMapper = useMemo(
    () => createValueMapper(intl, getCaseItemTypeTranslationId),
    [intl],
  )

  const transformCaseItemStatusItems = useMemo(
    () => createItemsTransformer(intl, getCaseItemStatusTranslationId),
    [intl],
  )

  const caseItemStatusValueMapper = useMemo(
    () => createValueMapper(intl, getCaseItemStatusTranslationId),
    [intl],
  )

  return (
    <FilterColumn isVisible={isVisible} onClose={onCloseFilterColumn}>
      <MultiselectFilter
        filterProps={{
          attribute: 'case_item.type',
          transformItems: transformCaseItemTypeItems,
        }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_type',
        })}
        selectProps={{
          iconProps: {
            icon: faCartShopping,
          },
          instanceId: E2E_ORDER_FILTER_TYPE,
        }}
        valueMapper={caseItemTypeValueMapper}
      />

      <MultiselectFilter
        filterProps={{
          attribute: 'case_item.status',
          transformItems: transformCaseItemStatusItems,
        }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_status',
        })}
        selectProps={{
          iconProps: {
            icon: faBarsProgress,
          },
          instanceId: E2E_ORDER_FILTER_STATUS,
        }}
        valueMapper={caseItemStatusValueMapper}
      />

      <MultidatepickerFilter
        autoComplete="off"
        filterProps={{ attribute: 'case_item.created' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_created',
        })}
        multidatePickerProps={{
          e2eSelector: E2E_ORDER_FILTER_DATE,
          maxDate: new Date().setHours(23, 59, 59),
          toolbarE2eSelector: E2E_ORDER_FILTER_DATE_TOOLBAR,
          /* eslint-disable sort-keys-fix/sort-keys-fix */
          customToolbarConfig: {
            today: {
              scope: 'today',
            },
            last_7days: {
              length: 7,
              period: 'days',
              scope: 'past',
            },
            last_30days: {
              length: 30,
              period: 'days',
              scope: 'past',
            },
            last_6months: {
              length: 6,
              period: 'months',
              scope: 'past',
            },
          },
          /* eslint-enable sort-keys-fix/sort-keys-fix */
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'case_item.device_definition_name' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_device_definition_name',
        })}
        selectProps={{
          iconProps: {
            icon: faTags,
          },
          instanceId: E2E_ORDER_FILTER_MODEL,
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'case_item.device_definition_manufacturer' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_device_definition_manufacturer',
        })}
        selectProps={{
          iconProps: {
            icon: faTrademark,
          },
          instanceId: E2E_ORDER_FILTER_MANUFACTURER,
        }}
      />

      <MultiselectFilter
        filterProps={{ attribute: 'case_item.device_definition_product_type' }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_device_definition_type',
        })}
        selectProps={{
          iconProps: {
            icon: faLaptopMobile,
          },
          instanceId: E2E_ORDER_FILTER_DEVICE_TYPE,
        }}
      />

      {isTaggingEnabled && (
        <MultiselectFilter
          filterProps={{ attribute: 'case_item.tags.name' }}
          label={intl.formatMessage({
            id: 'typesenseAttribute_case_item_tags_name',
          })}
          selectProps={{
            iconProps: {
              icon: faTag,
            },
            instanceId: E2E_ORDER_FILTER_TAGS,
          }}
        />
      )}

      <RangeFilter
        filterProps={{
          attribute: 'case_item.copayment',
          precision: 2,
        }}
        label={intl.formatMessage({
          id: 'typesenseAttribute_case_item_copayment',
        })}
        rangeInputProps={{
          e2eSelector: E2E_ORDER_FILTER_COPAYMENT,
          range: true,
          step: 1,
          type: 'input',
        }}
      />
    </FilterColumn>
  )
})
